import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, ContentChild, ElementRef, input, Input, signal, ViewChild } from '@angular/core';
import { ClickOutsideDirective } from 'shared/directives/click-outside.directive';

type PositioningOptions = 'top' | 'bottom' | 'left' | 'right';

/**
 * This component produces a menu that's activated by an element that has the menuActivator attribute
 * Usage:
 * <app-menu positioning=['top', 'right']>
 *     <span menuActivator>This element can be anything</span>
 *     <!-- The content of the menu here-->
 * </app-menu>
 *
 * Future possible improvement: dynamically compute positioning
 */

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    imports: [NgClass, ClickOutsideDirective],
})
export class MenuComponent {
    @ViewChild('collapsibleMenu') collapsibleMenu!: ElementRef;
    @ContentChild('menuActivatorReference') menuActivatorReference!: ElementRef;

    @Input() set positioning(value: PositioningOptions[]) {
        this._positioning = value;
        const tempPositioningObject: { [key: string]: boolean } = {};

        for (let i = 0; i < value.length; i++) {
            const key = value[i] as string;
            tempPositioningObject[key] = true;
        }

        // Object needs to be immutable for dirty checking to work properly
        this.positionClasses = {
            ...this.positionClasses,
            ...tempPositioningObject,
        };
    }

    get positioning() {
        return this._positioning;
    }

    disabled = input<boolean>(false);

    private _positioning: PositioningOptions[] = [];

    public positionClasses: { [key: string]: boolean } = {
        top: false,
        bottom: false,
        left: false,
        right: false,
    };

    public menuOpen = signal<boolean>(false);
    public hidden = true;

    constructor(private cdr: ChangeDetectorRef) {}

    openMenu(event: MouseEvent) {
        event.stopPropagation();
        this.menuOpen.set(true);
        this.cdr.detectChanges();
        // Detect changes needed to update the DOM and render the ViewChild collapsibleMenu

        if (this.menuActivatorReference) {
            const menuActivatorWidth = this.menuActivatorReference?.nativeElement?.offsetWidth;

            if (menuActivatorWidth && this.collapsibleMenu?.nativeElement && this.menuOpen()) {
                const margin = '14px';
                const iconWidths = 24;

                this.collapsibleMenu.nativeElement.style.width = menuActivatorWidth + iconWidths + 'px';
                this.collapsibleMenu.nativeElement.style.right = margin;
                this.hidden = false;
            }
        }
    }

    public closeMenu() {
        this.menuOpen.set(false);
        this.hidden = true;
    }
}
